/**
 * ========================================
 * FORMULARIO UNIFICADO DE TRÁMITES VEHICULARES
 * ========================================
 *
 * Soporta:
 * - Salida de Patios
 * - Individualización
 * - Impugnación
 *
 * Framework: Alpine.js
 * Arquitectura: Autocontenido (sin dependencias externas)
 * @author Sistema Unificado
 * @since 2025
 */

// ========================================
// MÓDULOS INLINE - HELPERS
// ========================================

/**
 * Sistema de Alertas usando SweetAlert2
 */
const TramiteAlerts = {
    success(title, message) {
        return window.Swal.fire({
            icon: "success",
            title,
            text: message,
            confirmButtonColor: "#2563eb",
            confirmButtonText: "Entendido",
        });
    },

    error(title, message) {
        return window.Swal.fire({
            icon: "error",
            title,
            text: message,
            confirmButtonColor: "#2563eb",
            confirmButtonText: "Entendido",
        });
    },

    warning(title, message) {
        return window.Swal.fire({
            icon: "warning",
            title,
            text: message,
            confirmButtonColor: "#2563eb",
            confirmButtonText: "Entendido",
        });
    },

    // Muestra un overlay ligero de carga (DOM) que bloquea interacciones
    // pero se sitúa por debajo de SweetAlert para no impedir otras alertas.
    loading(title = "Cargando...") {
        try {
            const id = "tramite-loading-overlay";
            let el = document.getElementById(id);
            if (!el) {
                el = document.createElement("div");
                el.id = id;
                el.style.position = "fixed";
                el.style.left = "0";
                el.style.top = "0";
                el.style.right = "0";
                el.style.bottom = "0";
                el.style.display = "flex";
                el.style.alignItems = "center";
                el.style.justifyContent = "center";
                el.style.background = "rgba(255,255,255,0.6)";
                // Z-index por debajo de SweetAlert (Swal suele usar >1050)
                el.style.zIndex = "1040";
                el.innerHTML = `
                    <div style="display:flex;flex-direction:column;align-items:center;gap:8px;">
                        <div style="border:4px solid rgba(0,0,0,0.08);border-left-color:#2563eb;border-radius:50%;width:40px;height:40px;animation:tramite-spin 1s linear infinite"></div>
                        <div style="font-weight:600;color:#111">${title}</div>
                    </div>
                `;
                document.body.appendChild(el);

                if (!document.getElementById("tramite-loading-style")) {
                    const style = document.createElement("style");
                    style.id = "tramite-loading-style";
                    style.innerHTML = `@keyframes tramite-spin{to{transform:rotate(360deg)}}`;
                    document.head.appendChild(style);
                }
            } else {
                el.style.display = "flex";
            }
        } catch (e) {
            // ignore
        }
    },

    // Oculta el overlay de carga (no cierra SweetAlert)
    close() {
        try {
            const el = document.getElementById("tramite-loading-overlay");
            if (el) el.style.display = "none";
        } catch (e) {
            // ignore
        }
    },

    mostrarErroresValidacion(errores) {
        const labels = {
            documento_infractor: "Documento del infractor",
            identificacion: "Identificación",
            email: "Correo",
            celular: "Celular",
            fecha_inmovilizacion: "Fecha",
            placa: "Placa",
        };

        const humanize = (campo) => labels[campo] || campo.replace(/_/g, " ");

        const html =
            '<ul class="text-start">' +
            Object.entries(errores)
                .map(([campo, mensajes]) => {
                    const arr = Array.isArray(mensajes) ? mensajes : [mensajes];
                    return arr
                        .map(
                            (m) =>
                                `<li><strong>${humanize(
                                    campo
                                )}:</strong> ${m}</li>`
                        )
                        .join("");
                })
                .join("") +
            "</ul>";

        return window.Swal.fire({
            icon: "error",
            title: "Errores de Validación",
            html,
            confirmButtonColor: "#2563eb",
            confirmButtonText: "Revisar",
        });
    },

    mostrarErroresBackend(errores, title = "Errores") {
        const html =
            '<ul class="text-start">' +
            Object.entries(errores)
                .map(([campo, mensajes]) => {
                    const msgs = Array.isArray(mensajes)
                        ? mensajes
                        : [mensajes];
                    return msgs
                        .map(
                            (msg) =>
                                `<li><strong>${campo}:</strong> ${msg}</li>`
                        )
                        .join("");
                })
                .join("") +
            "</ul>";

        return window.Swal.fire({
            icon: "error",
            title,
            html,
            confirmButtonColor: "#2563eb",
            confirmButtonText: "Revisar",
        });
    },
};

/**
 * Sistema de API para comunicación con el backend
 */
const TramiteAPI = {
    async fetchAPI(url, data = {}) {
        const csrfToken = document.querySelector(
            'meta[name="csrf-token"]'
        )?.content;

        const response = await fetch(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
                "X-CSRF-TOKEN": csrfToken,
                "X-Requested-With": "XMLHttpRequest",
            },
            body: JSON.stringify(data),
        });

        if (!response.ok) {
            // Intentar leer el mensaje del backend antes de lanzar excepción
            try { return await response.json(); } catch { throw new Error(`HTTP ${response.status}`); }
        }

        return await response.json();
    },

    async enviarSolicitud(formData, url) {
        const csrfToken = document.querySelector(
            'meta[name="csrf-token"]'
        )?.content;

        if (csrfToken) {
            formData.append("_token", csrfToken);
        }

        const response = await fetch(url, {
            method: "POST",
            body: formData,
            headers: {
                "X-Requested-With": "XMLHttpRequest",
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
        }

        return await response.json();
    },
};

/**
 * Validadores de formularios
 */
const TramiteValidators = {
    validarEmail(email) {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    },
    describirErrorEmail(email) {
        if (!email) return "El correo es requerido";
        if (email.length > 100)
            return "Correo demasiado largo (máx 100 caracteres)";
        if (!this.validarEmail(email)) return "Formato de correo inválido";
        return null;
    },

    validarCelular(celular) {
        return /^3\d{9}$/.test(celular);
    },
    describirErrorCelular(celular) {
        if (!celular) return "El celular es requerido";
        if (/\D/.test(celular))
            return "Formato inválido: solo se permiten números";
        if (celular.length !== 10) return "El celular debe tener 10 dígitos";
        if (!/^3\d{9}$/.test(celular))
            return "Celular inválido: debe iniciar con 3 y tener 10 dígitos";
        return null;
    },

    validarNombre(nombre) {
        // Permitir letras (incluye acentos) y espacios, mínimo 2 caracteres
        return /^[A-Za-zÀ-ÖØ-öø-ÿ\s]{2,50}$/.test(nombre?.trim() ?? "");
    },

    describirErrorNombre(nombre) {
        if (!nombre || nombre.trim().length === 0)
            return "El nombre es requerido";
        if (nombre.trim().length < 2)
            return "Demasiado corto: mínimo 2 caracteres";
        if (nombre.trim().length > 50)
            return "Demasiado largo: máximo 50 caracteres";
        if (!/^[A-Za-zÀ-ÖØ-öø-ÿ\s]+$/.test(nombre))
            return "Formato inválido: solo letras y espacios";
        return null;
    },

    validarDocumento(doc) {
        return /^\d{5,15}$/.test(doc);
    },
    describirErrorDocumento(doc) {
        if (!doc) return "El documento es requerido";
        if (/\D/.test(doc)) return "Formato inválido: solo se permiten números";
        if (doc.length < 5) return "Demasiado corto: mínimo 5 dígitos";
        if (doc.length > 15) return "Demasiado largo: máximo 15 dígitos";
        return null;
    },

    validarFecha(fechaStr) {
        if (!fechaStr) return false;
        const ts = Date.parse(fechaStr);
        if (Number.isNaN(ts)) return false;

        const fecha = new Date(ts);
        const hoy = new Date();
        hoy.setHours(0, 0, 0, 0);

        // Solo validar que no sea fecha futura
        return fecha <= hoy;
    },

    describirErrorFecha(fechaStr) {
        if (!fechaStr) return "La fecha es requerida";
        const ts = Date.parse(fechaStr);
        if (Number.isNaN(ts)) return "Formato de fecha inválido";

        const fecha = new Date(ts);
        const hoy = new Date();
        hoy.setHours(0, 0, 0, 0);

        if (fecha > hoy) return "La fecha no puede ser futura";

        return null;
    },

    getReglas(step, tipo) {
        // Paso 1: Datos del comparendo (solo para salida)
        if (step === 1 && tipo === "salida") {
            return [
                {
                    campo: "fecha_inmovilizacion",
                    cond: (d) => !d.fecha_inmovilizacion,
                    msg: "La fecha de inmovilización es requerida",
                },
                {
                    campo: "documento_infractor",
                    cond: (d) => !d.documento_infractor,
                    msg: "El documento del infractor es requerido",
                },
                {
                    campo: "documento_infractor",
                    cond: (d) =>
                        d.documento_infractor &&
                        !this.validarDocumento(d.documento_infractor),
                    msg: "Documento inválido (5-15 dígitos)",
                },
            ];
        }

        // Paso 2: Datos del solicitante (todos los tipos)
        if (step === 2) {
            return [
                {
                    campo: "tipo",
                    cond: (d) => !d.tipo,
                    msg: "Seleccione el tipo de solicitante",
                },
                {
                    campo: "identificacion",
                    cond: (d) => !d.identificacion,
                    msg: "La identificación es requerida",
                },
                {
                    campo: "identificacion",
                    cond: (d) =>
                        d.identificacion &&
                        !this.validarDocumento(d.identificacion),
                    msg: "Identificación inválida (5-15 dígitos)",
                },
                {
                    campo: "nombres",
                    cond: (d) => !d.nombres || d.nombres.trim().length < 2,
                    msg: "Los nombres son requeridos (mínimo 2 caracteres)",
                },
                {
                    campo: "apellidos",
                    cond: (d) => !d.apellidos || d.apellidos.trim().length < 2,
                    msg: "Los apellidos son requeridos (mínimo 2 caracteres)",
                },
                {
                    campo: "celular",
                    cond: (d) => !d.celular,
                    msg: "El celular es requerido",
                },
                {
                    campo: "celular",
                    cond: (d) => d.celular && !this.validarCelular(d.celular),
                    msg: "Celular inválido (debe tener 10 dígitos)",
                },
                {
                    campo: "email",
                    cond: (d) => !d.email,
                    msg: "El correo es requerido",
                },
                {
                    campo: "email",
                    cond: (d) => d.email && !this.validarEmail(d.email),
                    msg: "correo inválido",
                },
                {
                    campo: "direccion",
                    cond: (d) => !d.direccion || d.direccion.trim().length < 5,
                    msg: "La dirección es requerida (mínimo 5 caracteres)",
                },
                {
                    campo: "departamento",
                    cond: (d) => !d.departamento,
                    msg: "El departamento es requerido",
                },
                {
                    campo: "ciudad",
                    cond: (d) => !d.ciudad,
                    msg: "La ciudad es requerida",
                },
                {
                    campo: "politica_datos",
                    cond: (d) => !d.politica_datos,
                    msg: "Debe aceptar la política de datos",
                },
                {
                    campo: "politica_email",
                    cond: (d) => !d.politica_email,
                    msg: "Debe aceptar el uso de email",
                },
            ];
        }

        return [];
    },
};

/**
 * Sistema de limpieza de archivos temporales
 */
function initCleanup(config) {
    let solicitudEnviada = false;
    const { deleteRoute } = config;

    const limpiar = () => {
        if (solicitudEnviada) return;

        const filepath = document.querySelector(".dropzone-filepath")?.value;
        if (!filepath) return;

        const formData = new FormData();
        formData.append("folder", filepath);
        formData.append(
            "_token",
            document.querySelector('meta[name="csrf-token"]')?.content
        );

        navigator.sendBeacon(deleteRoute, formData);
        console.log("🗑️ Limpieza de archivos temporales");
    };

    window.addEventListener("beforeunload", limpiar);
    window.addEventListener("inmovilizado-enviado", () => {
        solicitudEnviada = true;
    });

    return {
        marcarEnviado: () => {
            solicitudEnviada = true;
        },
    };
}

// ========================================
// COMPONENTE PRINCIPAL
// ========================================

export function tramiteForm(tipo = "salida") {
    return {
        // ========================================
        // ESTADO DEL FORMULARIO
        // ========================================
        tipo: tipo,
        placaValida: false,
        placa: "",
        step: 1,
        isLoading: false,
        touched: {},
        errores: {},

        // ========================================
        // DATOS DEL FORMULARIO
        // ========================================
        formData: {
            placa: "",
            fecha_inmovilizacion: "",
            documento_infractor: "",
            tipo: "",
            identificacion: "",
            nombres: "",
            apellidos: "",
            celular: "",
            email: "",
            direccion: "",
            departamento: "",
            ciudad: "",
            politica_datos: false,
            politica_email: false,
        },

        // ========================================
        // DATOS AUXILIARES
        // ========================================
        municipios: window.TRAMITE_CONFIG?.municipios || {},
        ciudadesDisponibles: [],
        documentosSeleccionados: [],
        requisitosData: window.TRAMITE_CONFIG?.requisitos || {},
        choicesDepartamento: null,
        choicesCiudad: null,
        cleanupInstance: null,

        // ========================================
        // INICIALIZACIÓN
        // ========================================
        init() {
            console.log(`🚀 Formulario de ${this.tipo} iniciado`);
            
            // Limpiar estado persistente de Alpine.js al cargar
            this.limpiarEstadoPersistente();
            
            this.setupWatchers();
            this.initCleanup();

            // Watch para inicializar Choices.js cuando el paso 2 sea visible
            this.$watch("step", (newStep) => {
                if (newStep === 2) {
                    this.initChoicesWhenVisible();
                }
            });
        },

        /**
         * Limpia el estado persistente al cargar la página
         * Evita que autocompletado del navegador o Alpine.js persistan datos antiguos
         */
        limpiarEstadoPersistente() {
            // Resetear estado completamente
            this.placaValida = false;
            this.placa = "";
            this.step = 1;
            this.touched = {};
            this.errores = {};
            this.documentosSeleccionados = [];
            this.ciudadesDisponibles = [];
            
            // Limpiar formData
            Object.keys(this.formData).forEach((key) => {
                this.formData[key] = [
                    "politica_datos",
                    "politica_email",
                ].includes(key)
                    ? false
                    : "";
            });
            
            // Limpiar inputs del DOM (autocompletado del navegador)
            this.$nextTick(() => {
                const inputs = this.$el.querySelectorAll('input:not([type="radio"]):not([type="checkbox"])');
                inputs.forEach(input => {
                    if (input.value && !input.disabled && !input.readOnly) {
                        input.value = '';
                    }
                });
                
                // Limpiar selects
                const selects = this.$el.querySelectorAll('select');
                selects.forEach(select => {
                    if (select.value) {
                        select.value = '';
                    }
                });
            });
            
            console.log('🧹 Estado persistente limpiado');
        },

        /**
         * Inicializa el sistema de limpieza automática de archivos
         */
        initCleanup() {
            const deleteRoute = `/servicios/${this.getRoutePrefix()}/delete-tmp-folder`;
            this.cleanupInstance = initCleanup({ deleteRoute });
        },

        /**
         * Configura watchers reactivos para validación en tiempo real
         */
        setupWatchers() {
            const camposCriticos = [
                "celular",
                "email",
                "identificacion",
                "documento_infractor",
                "nombres",
                "apellidos",
                "direccion",
            ];

            camposCriticos.forEach((campo) => {
                this.$watch(`formData.${campo}`, (valor) => {
                    // Marcar como touched cuando el usuario empiece a escribir
                    if (valor && valor.length > 0) {
                        this.touched[campo] = true;
                    }

                    // Validar en tiempo real si el campo ya fue touched
                    if (this.touched[campo]) {
                        this.validarCampo(campo);
                    }
                });
            });

            // Watchers para checkboxes
            this.$watch("formData.politica_datos", () => {
                if (this.touched["politica_datos"]) {
                    this.validarCampo("politica_datos");
                }
            });

            this.$watch("formData.politica_email", () => {
                if (this.touched["politica_email"]) {
                    this.validarCampo("politica_email");
                }
            });
        },

        // ========================================
        // BÚSQUEDA DE PLACA (PASO INICIAL)
        // ========================================
        async buscarPlaca() {
            if (!this.validarPlaca()) {
                return;
            }

            // Validaciones adicionales según el tipo (mensajes más específicos)
            if (
                this.tipo === "individualizacion" ||
                this.tipo === "impugnacion"
            ) {
                const msgFecha = TramiteValidators.describirErrorFecha(this.formData.fecha_inmovilizacion);
                if (msgFecha) {
                    const tipoMsgs = {
                        salida: 'No se pudo validar la salida para la placa. Intente nuevamente',
                        individualizacion: 'No se pudo validar la individualización. Intente nuevamente',
                        impugnacion: 'No se pudo validar la impugnación. Intente nuevamente'
                    };
                    const tipoMsg = tipoMsgs[this.tipo] || 'No se pudo validar la placa. Intente nuevamente';
                    TramiteAlerts.warning('Validación', tipoMsg);
                    return;
                }
            }

            if (this.tipo === "impugnacion") {
                const msgDoc = TramiteValidators.describirErrorDocumento(
                    this.formData.documento_infractor
                );
                if (msgDoc) {
                    const tipoMsgs = {
                        salida: 'No se pudo validar la salida para la placa. Intente nuevamente',
                        individualizacion: 'No se pudo validar la individualización. Intente nuevamente',
                        impugnacion: 'No se pudo validar la impugnación. Intente nuevamente'
                    };
                    const tipoMsg = tipoMsgs[this.tipo] || 'No se pudo validar la placa. Intente nuevamente';
                    TramiteAlerts.warning('Validación', tipoMsg);
                    return;
                }
            }

            // En step 1 evitamos el overlay global para que alertas no se sobrepongan
            this.isLoading = true;

            try {
                // Construir datos a enviar según el tipo
                const data = { placa: this.placa };

                if (this.tipo === "individualizacion") {
                    data.fecha = this.formData.fecha_inmovilizacion;
                } else if (this.tipo === "impugnacion") {
                    data.fecha = this.formData.fecha_inmovilizacion;
                    data.cedula = this.formData.documento_infractor;
                }

                const response = await TramiteAPI.fetchAPI(
                    `/servicios/${this.getRoutePrefix()}/validar-placa`,
                    data
                );

                if (response.success) {
                    this.placaValida = true;
                    this.formData.placa = this.placa;

                    // Para individualización e impugnación, saltar directo al paso 2 (datos del solicitante)
                    if (
                        this.tipo === "individualizacion" ||
                        this.tipo === "impugnacion"
                    ) {
                        this.step = 2;
                    }

                    // NO mostrar alerta de éxito - transición automática
                } else {
                    // Mostrar alerta solo cuando hay error
                    const msg =
                        response.message ||
                        "No se encontró la placa o los datos no coinciden";
                    TramiteAlerts.error("Error de Validación", msg);
                }
            } catch (error) {
                console.error("Error al validar:", error);
                // Mensaje contextual según el tipo de trámite
                const tipoMsgs = {
                    salida: "No se pudo validar la  informacion para la salida. Revise e intente de nuevo",
                    individualizacion:
                        "No se pudo validar la  informacion  para la individualización. Revise e intente de nuevo",
                    impugnacion:
                        "No se pudo validar la  informacion  para la impugnación. Revise e intente de nuevo",
                };
                const tipoMsg =
                    tipoMsgs[this.tipo] ||
                    "No se pudo validar la placa. Intente nuevamente";
                TramiteAlerts.error("Error", tipoMsg);
            } finally {
                TramiteAlerts.close();
                this.isLoading = false;
            }
        },

        /**
         * Valida el formato de la placa
         * @returns {boolean} true si la placa es válida
         */
        validarPlaca() {
            if (!this.placa || this.placa.trim() === "") {
                TramiteAlerts.warning(
                    "Campo Requerido",
                    "Debe ingresar la placa del vehículo"
                );
                return false;
            }

            if (this.placa.length !== 6) {
                TramiteAlerts.warning(
                    "Formato Inválido",
                    "La placa debe tener exactamente 6 caracteres"
                );
                return false;
            }

            return true;
        },

        // ========================================
        // NAVEGACIÓN ENTRE PASOS
        // ========================================
        
        /**
         * Verifica si se puede retroceder al paso anterior
         * Para individualización/impugnación que inician en paso 2, no hay paso previo
         */
        puedeRetroceder() {
            if (this.step <= 1) return false;
            
            // Si estamos en paso 2 y es individualización/impugnación, no hay paso previo
            if (this.step === 2 && (this.tipo === 'individualizacion' || this.tipo === 'impugnacion')) {
                return false;
            }
            
            return true;
        },
        
        async nextStep() {
            // Evitar múltiples envíos concurrentes
            if (this.isLoading) return;

            // Validar frontend
            if (!this.validarPasoActual()) {
                return;
            }

            this.errores = {};
            this.isLoading = true;
            TramiteAlerts.loading("Validando...");

            // Para individualización/impugnación: paso 2 en frontend = paso 2 en backend (se salta el paso 1)
            const backendStep =
                (this.tipo === "individualizacion" || this.tipo === "impugnacion") && this.step === 2
                    ? 2
                    : this.step;
            const endpoint = `/servicios/${this.getRoutePrefix()}/validar-paso${backendStep}`;

            try {
                const response = await TramiteAPI.fetchAPI(
                    endpoint,
                    this.formData
                );

                if (response.success) {
                    this.step++;
                    window.scrollTo({ top: 0, behavior: "smooth" });
                } else {
                    // Guardar errores del backend en el estado reactivo
                    this.errores = response.errors || {};

                    // Asegurar que touched sea un objeto
                    if (!this.touched || typeof this.touched !== 'object') {
                        this.touched = {};
                    }

                    // Si hay errores, marcar como "touched" los campos correspondientes
                    if (this.errores && Object.keys(this.errores).length > 0) {
                        Object.keys(this.errores).forEach((campo) => {
                            // Marcar el campo como tocado para que el UI muestre el error inline
                            this.touched[campo] = true;
                        });

                        // Intentar enfocar el primer campo inválido para mejorar UX
                        const firstKey = Object.keys(this.errores)[0];
                        let firstEl = document.querySelector('[name="' + firstKey + '"]');
                        if (!firstEl) {
                            firstEl = document.querySelector('[x-model="formData.' + firstKey + '"]');
                        }
                        if (firstEl) {
                            try {
                                firstEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                firstEl.focus({ preventScroll: true });
                            } catch (e) {
                                try { firstEl.focus(); } catch (err) { /* ignore */ }
                            }
                        }

                        // Mostrar resumen de errores (alerta) pero mantener los errores inline visibles
                        TramiteAlerts.mostrarErroresBackend(
                            this.errores,
                            "Errores de Validación"
                        );
                    } else {
                        TramiteAlerts.error(
                            "Error",
                            response.message || "Error al validar los datos"
                        );
                    }
                }
            } catch (error) {
                console.error("Error en validación:", error);
                TramiteAlerts.error(
                    "Error",
                    "Error al validar los datos. Intente de nuevo"
                );
            } finally {
                TramiteAlerts.close();
                this.isLoading = false;
            }
        },

        prevStep() {
            // Verificar si realmente se puede retroceder
            if (!this.puedeRetroceder()) {
                console.warn('No se puede retroceder desde este paso');
                return;
            }
            
            // Mostrar modal de carga breve para bloquear la UI durante la transición
            TramiteAlerts.loading("Cargando...");
            this.step--;
            window.scrollTo({ top: 0, behavior: "smooth" });

            // Cerrar el modal después de dar tiempo al scroll/transición
            setTimeout(() => {
                TramiteAlerts.close();
            }, 350);
        },

        // ========================================
        // ENVÍO FINAL
        // ========================================
        async finalizarEnvio() {
            const totalRequisitos = Object.keys(this.requisitosData).length;

            // Validar documentos seleccionados
            if (!this.validarDocumentos(totalRequisitos)) {
                return;
            }

            // Validar archivo PDF
            const filepath =
                document.querySelector(".dropzone-filepath")?.value || "";
            const filename =
                document.querySelector(".dropzone-filename")?.value || "";

            if (!this.validarArchivoPDF(filepath)) {
                return;
            }

            this.isLoading = true;
            TramiteAlerts.loading("Enviando solicitud...");

            const formData = this.construirFormData(filepath, filename);

            try {
                const url = `/servicios/${this.getRoutePrefix()}/enviar`;
                const response = await TramiteAPI.enviarSolicitud(
                    formData,
                    url
                );

                if (response.success) {
                    TramiteAlerts.close();
                    // Marcar como enviado exitosamente (para no limpiar archivos)
                    if (window.marcarInmovilizadoEnviado) {
                        window.marcarInmovilizadoEnviado();
                    }

                    // Normalizar mensaje final: si la respuesta trae una clave/markup no fiable, usar fallback
                    const raw = response.message || "";
                    const looksBad = /{{|}}|:\w|<[^>]+>|^[^\s]{0,8}$/.test(raw);
                    const finalMsg =
                        !raw || looksBad
                            ? "La solicitud ha sido enviada exitosamente"
                            : raw;

                    await TramiteAlerts.success(
                        "¡Solicitud Enviada!",
                        finalMsg
                    );

                    this.resetForm();
                } else {
                    TramiteAlerts.close();
                    this.manejarErrorEnvio(response);
                }
            } catch (error) {
                console.error("Error al enviar:", error);
                TramiteAlerts.error(
                    "Error",
                    "Error al enviar la solicitud. Intente nuevamente"
                );
            } finally {
                TramiteAlerts.close();
                this.isLoading = false;
            }
        },

        /**
         * Valida que todos los documentos estén seleccionados
         * @param {number} total - Total de documentos requeridos
         * @returns {boolean}
         */
        validarDocumentos(total) {
            // Validar que se hayan seleccionado documentos
            if (this.documentosSeleccionados.length === 0) {
                TramiteAlerts.warning(
                    "Documentos Requeridos",
                    "Debe seleccionar los documentos del listado"
                );
                return false;
            }

            // VALIDACIÓN ESTRICTA: Deben estar TODOS seleccionados
            if (total > 0 && this.documentosSeleccionados.length < total) {
                TramiteAlerts.warning(
                    "Documentos Incompletos",
                    `Debe seleccionar todos los documentos requeridos (${total} documentos)`
                );
                return false;
            }

            return true;
        },

        /**
         * Valida que el archivo PDF esté adjunto
         * @param {string} filePath - Ruta del archivo
         * @returns {boolean}
         */
        validarArchivoPDF(filePath) {
            // Si hay un archivo subiendo, pedir al usuario que espere
            if (document.querySelector(".pdf-file.uploading")) {
                TramiteAlerts.warning(
                    "Archivo Cargando",
                    "El archivo se está subiendo, espere un momento e intente de nuevo"
                );
                return false;
            }

            if (!filePath) {
                TramiteAlerts.warning(
                    "Archivo Requerido",
                    "Debe adjuntar el archivo PDF con los documentos digitalizados"
                );
                return false;
            }
            return true;
        },

        /**
         * Construye el FormData para enviar
         * @param {string} filePath - Ruta del archivo
         * @param {string} filename - Nombre del archivo
         * @returns {FormData}
         */
        construirFormData(filePath, filename) {
            const formData = new FormData();

            Object.keys(this.formData).forEach((key) => {
                formData.append(key, this.formData[key]);
            });

            // Enviar documentos como array (uno por uno)
            this.documentosSeleccionados.forEach((doc, index) => {
                formData.append(`documentos[${index}]`, doc);
            });

            formData.append("filepath", filePath);
            formData.append("filename", filename);

            return formData;
        },

        /**
         * Maneja errores al enviar el formulario
         * @param {Object} response - Respuesta del servidor
         */
        manejarErrorEnvio(response) {
            this.errores = response.errors || {};

            if (Object.keys(this.errores).length > 0) {
                TramiteAlerts.mostrarErroresBackend(
                    this.errores,
                    "Error al Enviar Solicitud"
                );
                this.scrollAPrimerError();
            } else {
                TramiteAlerts.error(
                    "Error",
                    response.message || "No se pudo enviar la solicitud"
                );
            }
        },

        /**
         * Hace scroll al primer campo con error
         */
        scrollAPrimerError() {
            setTimeout(() => {
                const primerError = Object.keys(this.errores)[0];
                const elemento = document.querySelector(
                    `[name="${primerError}"]`
                );

                if (elemento) {
                    elemento.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                    });
                    elemento.focus();
                }
            }, 100);
        },

        // ========================================
        // RESETEO DE FORMULARIO
        // ========================================
        resetForm() {
            this.placaValida = false;
            this.placa = "";
            this.step = 1;
            this.touched = {};
            this.errores = {};
            Object.keys(this.formData).forEach((key) => {
                this.formData[key] = [
                    "politica_datos",
                    "politica_email",
                ].includes(key)
                    ? false
                    : "";
            });
            this.documentosSeleccionados = [];
            window.dispatchEvent(new Event("resetDropzones"));
        },

        // ========================================
        // CARGA DINÁMICA DE CIUDADES
        // ========================================
        cargarCiudades() {
            const departamento = this.formData.departamento;

            // Limpiar ciudad al cambiar departamento
            this.formData.ciudad = "";

            this.ciudadesDisponibles =
                departamento && this.municipios[departamento]
                    ? Object.entries(this.municipios[departamento]).map(
                          ([codigo, nombre]) => ({
                              codigo,
                              nombre,
                          })
                      )
                    : [];

            this.formData.ciudad = "";
        },

        // ========================================
        // GESTIÓN DE DOCUMENTOS
        // ========================================
        toggleDoc(id) {
            const index = this.documentosSeleccionados.indexOf(id);
            if (index > -1) {
                this.documentosSeleccionados.splice(index, 1);
            } else {
                this.documentosSeleccionados.push(id);
            }
        },

        // ========================================
        // VALIDACIÓN DE CAMPOS
        // ========================================

        /**
         * Obtiene las reglas de validación del paso actual
         */
        getReglas() {
            return TramiteValidators.getReglas(this.step, this.tipo);
        },

        /**
         * Valida todos los campos del paso actual
         * @returns {boolean} true si no hay errores
         */
        validarPasoActual() {
            const reglas = this.getReglas();
            this.errores = {};
            
            // Asegurar que touched sea un objeto
            if (!this.touched || typeof this.touched !== 'object') {
                this.touched = {};
            }

            reglas.forEach((regla) => {
                if (regla.cond(this.formData)) {
                    if (!Array.isArray(this.errores[regla.campo])) {
                        this.errores[regla.campo] = [];
                    }

                    // Marcar el campo como tocado para mostrar el error inline
                    this.touched[regla.campo] = true;

                    // Mejorar mensajes para campos que pueden fallar por formato o por longitud
                    let message = regla.msg;
                    if (
                        ["documento_infractor", "identificacion"].includes(
                            regla.campo
                        )
                    ) {
                        const specific =
                            TramiteValidators.describirErrorDocumento(
                                this.formData[regla.campo]
                            );
                        if (specific) message = specific;
                    } else if (["nombres", "apellidos"].includes(regla.campo)) {
                        const specific = TramiteValidators.describirErrorNombre(
                            this.formData[regla.campo]
                        );
                        if (specific) message = specific;
                    } else if (regla.campo === "fecha_inmovilizacion") {
                        const specific = TramiteValidators.describirErrorFecha(
                            this.formData.fecha_inmovilizacion
                        );
                        if (specific) message = specific;
                    } else if (regla.campo === "email") {
                        const specific = TramiteValidators.describirErrorEmail(
                            this.formData.email
                        );
                        if (specific) message = specific;
                    } else if (regla.campo === "celular") {
                        const specific =
                            TramiteValidators.describirErrorCelular(
                                this.formData.celular
                            );
                        if (specific) message = specific;
                    } else if (regla.campo === "fecha_inmovilizacion") {
                        const specific = TramiteValidators.describirErrorFecha(
                            this.formData.fecha_inmovilizacion
                        );
                        if (specific) message = specific;
                    }

                    this.errores[regla.campo].push(message);
                }
            });

            // Validaciones adicionales específicas para el Paso 3
            // - Requiere seleccionar TODOS los documentos requeridos
            // - Requiere adjuntar al menos un archivo PDF
            if (this.step === 3) {
                // Validar que se seleccionen TODOS los requisitos por id
                const requiredIds = Object.keys(this.requisitosData || {})
                    .map((k) => parseInt(k, 10))
                    .filter(Boolean);

                if (requiredIds.length > 0) {
                    const missing = requiredIds.filter((id) => !this.documentosSeleccionados.includes(id));
                    if (missing.length > 0) {
                        this.errores['documentos_seleccionados'] = [
                            `Debe seleccionar todos los documentos requeridos (${requiredIds.length} documentos)`,
                        ];
                    }
                }

                // Requerir al menos un archivo PDF adjunto
                const filepath = document.querySelector('.dropzone-filepath')?.value || '';
                const dropzoneEl = document.querySelector('[data-dropzone="true"]');
                const hasFile = Boolean(filepath) || (dropzoneEl && dropzoneEl.classList.contains('dz-started'));
                if (!hasFile) {
                    this.errores['archivo'] = [
                        'Debe adjuntar al menos un archivo PDF',
                    ];
                }
            }

            if (Object.keys(this.errores).length > 0) {
                // Enfocar el primer campo con error para mejorar UX
                const firstKey = Object.keys(this.errores)[0];
                let firstEl = document.querySelector('[name="' + firstKey + '"]');
                if (!firstEl) {
                    firstEl = document.querySelector('[x-model="formData.' + firstKey + '"]');
                }
                if (firstEl) {
                    try {
                        firstEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        setTimeout(() => {
                            try { firstEl.focus({ preventScroll: true }); } catch (e) {}
                        }, 300);
                    } catch (e) {
                        try { firstEl.focus(); } catch (err) { /* ignore */ }
                    }
                }
                
                TramiteAlerts.mostrarErroresValidacion(this.errores);
                return false;
            }

            return true;
        },

        /**
         * Valida un campo específico
         * @param {string} campo - Nombre del campo
         */
        validarCampo(campo) {
            const reglas = this.getReglas().filter((r) => r.campo === campo);
            delete this.errores[campo];

            for (const regla of reglas) {
                if (regla.cond(this.formData)) {
                    let message = regla.msg;
                    if (
                        ["documento_infractor", "identificacion"].includes(
                            campo
                        )
                    ) {
                        const specific =
                            TramiteValidators.describirErrorDocumento(
                                this.formData[campo]
                            );
                        if (specific) message = specific;
                    } else if (["nombres", "apellidos"].includes(campo)) {
                        const specific = TramiteValidators.describirErrorNombre(
                            this.formData[campo]
                        );
                        if (specific) message = specific;
                    } else if (campo === "email") {
                        const specific = TramiteValidators.describirErrorEmail(
                            this.formData.email
                        );
                        if (specific) message = specific;
                    } else if (campo === "celular") {
                        const specific =
                            TramiteValidators.describirErrorCelular(
                                this.formData.celular
                            );
                        if (specific) message = specific;
                    }

                    this.errores[campo] = [message];
                    break;
                }
            }
        },

        marcarTocado(campo) {
            this.touched[campo] = true;
            this.validarCampo(campo);
        },

        toUpperOnlyNumbers(event) {
            const cleaned = event.target.value.replace(/[^0-9]/g, "");
            event.target.value = cleaned;

            // Si el input tiene atributo name y existe en formData, sincronizar el modelo
            const name = event.target.name;
            if (
                name &&
                this.formData &&
                Object.prototype.hasOwnProperty.call(this.formData, name)
            ) {
                this.formData[name] = cleaned;
            }
        },

        toAlphaOnly(event) {
            // Permitir letras, espacios y acentos
            const cleaned = event.target.value.replace(
                /[^A-Za-zÀ-ÖØ-öø-ÿ\s]/g,
                ""
            );
            event.target.value = cleaned;

            const name = event.target.name;
            if (
                name &&
                this.formData &&
                Object.prototype.hasOwnProperty.call(this.formData, name)
            ) {
                this.formData[name] = cleaned;
            }
        },

        // ========================================
        // MODAL INFORMATIVO — dispara evento global
        // El componente <x-info-modal /> escucha 'info-modal:abrir'
        // ========================================
        abrirModal(clave) {
            const entrada = window.TRAMITE_CONFIG?.textos?.[clave];
            if (!entrada) return;
            window.dispatchEvent(new CustomEvent('info-modal:abrir', { detail: entrada }));
        },

        // ========================================
        // HELPER: PREFIJO DE RUTAS DINÁMICO
        // ========================================
        getRoutePrefix() {
            const prefixes = {
                salida: "salida", // ← Nueva ruta /servicios/salida
                individualizacion: "individualizar",
                impugnacion: "impugnacion",
            };
            return prefixes[this.tipo] || "salida";
        },

        // ========================================
        // INICIALIZACIÓN DE CHOICES.JS
        // ========================================

        /**
         * Inicializa Choices.js cuando el paso 2 es visible
         */
        initChoicesWhenVisible() {
            if (this.step !== 2) return;

            this.$nextTick(() => {
                // Destruir instancias previas de forma segura
                this.destruirChoices();
                
                // Inicializar nuevamente
                this.inicializarSelectDepartamento();
                this.inicializarSelectCiudad();
            });
        },
        
        /**
         * Destruye instancias de Choices.js de forma segura
         */
        destruirChoices() {
            // Cancelar listeners nativos previos antes de destruir
            if (this._abortDepto) { this._abortDepto.abort(); this._abortDepto = null; }
            if (this._abortCiudad) { this._abortCiudad.abort(); this._abortCiudad = null; }

            try {
                if (this.choicesDepartamento) {
                    this.choicesDepartamento.destroy();
                    this.choicesDepartamento = null;
                }
            } catch (e) {
                console.warn('Error al destruir choicesDepartamento:', e);
            }

            try {
                if (this.choicesCiudad) {
                    this.choicesCiudad.destroy();
                    this.choicesCiudad = null;
                }
            } catch (e) {
                console.warn('Error al destruir choicesCiudad:', e);
            }
        },

        /**
         * Inicializa el select de departamento
         */
        inicializarSelectDepartamento() {
            const select = document.getElementById("departamento-select");
            if (!select || typeof Choices === "undefined") return;

            // Evitar doble inicialización
            if (this.choicesDepartamento) {
                return;
            }

            // Verificar si ya tiene Choices inicializado
            if (select.classList.contains("choices__input")) {
                return;
            }

            this.choicesDepartamento = new Choices(select, {
                searchEnabled: true,
                searchFields: ['label'],
                shouldSort: false,
                itemSelectText: "",
                position: "bottom",
                searchPlaceholderValue: "Buscar departamento...",
                searchResultLimit: 50,
            });

            // AbortController garantiza un solo listener activo por ciclo
            this._abortDepto = new AbortController();
            select.addEventListener("change", (e) => {
                const value = e.detail?.value || e.target.value;
                this.formData.departamento = value;
                this.marcarTocado("departamento");
                this.cargarCiudades();
                this.reinicializarSelectCiudad();
            }, { signal: this._abortDepto.signal });
        },

        /**
         * Inicializa el select de ciudad
         */
        inicializarSelectCiudad() {
            const select = document.getElementById("ciudad-select");
            if (!select || typeof Choices === "undefined") return;

            // Evitar doble inicialización
            if (this.choicesCiudad) {
                return;
            }

            // Verificar si ya tiene Choices inicializado
            if (select.classList.contains("choices__input")) {
                return;
            }

            this.choicesCiudad = new Choices(select, {
                searchEnabled: true,
                searchFields: ['label'],
                shouldSort: false,
                itemSelectText: "",
                position: "bottom",
                searchPlaceholderValue: "Buscar ciudad...",
                searchResultLimit: 100,
            });

            // AbortController garantiza un solo listener activo por ciclo
            this._abortCiudad = new AbortController();
            select.addEventListener("change", (e) => {
                const value = e.detail?.value || e.target.value;
                this.formData.ciudad = value;
                this.marcarTocado("ciudad");
            }, { signal: this._abortCiudad.signal });
        },

        /**
         * Reinicializa el select de ciudad
         */
        reinicializarSelectCiudad() {
            if (this._abortCiudad) { this._abortCiudad.abort(); this._abortCiudad = null; }

            if (this.choicesCiudad && this.choicesCiudad.destroy) {
                try {
                    this.choicesCiudad.destroy();
                } catch (e) {
                    console.warn("Error al destruir Choices de ciudad:", e);
                }
                this.choicesCiudad = null;
            }

            this.$nextTick(() => {
                const select = document.getElementById("ciudad-select");
                if (select) {
                    this.inicializarSelectCiudad();
                }
            });
        },
    };
}
